/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

export const normalizeDesktopProjectId = (projectId: string | null | undefined) => {
  const normalizedProjectId = projectId?.trim();
  if (
    !normalizedProjectId ||
    normalizedProjectId === 'undefined' ||
    normalizedProjectId === 'null'
  ) {
    return null;
  }

  return normalizedProjectId;
};

/**
 * Windows whose content unmounts while out of view. PVSim stays warm while covered, but
 * still unmounts on minimize/close; the other apps also unmount under a maximized window.
 * Their apps keep everything they need to come back (drafts, listing state) in the app state
 * store, so unmounting frees their grids and charts.
 */
export const UNMOUNT_OUT_OF_VIEW_WINDOW_IDS = new Set(['pvsim', 'ess', 'pvcosting', 'simulate']);

export function shouldMountWindowContent({
  focusedWindowId,
  isClosing,
  isFocusedWindowMaximized,
  windowState,
}: {
  focusedWindowId: string | undefined;
  isClosing: boolean;
  isFocusedWindowMaximized: boolean;
  windowState: { id: string; isMinimized: boolean };
}) {
  if (!UNMOUNT_OUT_OF_VIEW_WINDOW_IDS.has(windowState.id)) {
    return true;
  }

  if (isClosing || windowState.isMinimized) {
    return false;
  }

  // PVSim's charts/graph are expensive to reconstruct on every maximize/restore. Keep their
  // state and chart effects warm while covered; the frame hides it from input/accessibility.
  // Minimize/close still release the content through the branch above.
  return (
    windowState.id === 'pvsim' || !isFocusedWindowMaximized || focusedWindowId === windowState.id
  );
}

/**
 * Whether a window's content is visible: open, not minimized and not covered by the focused
 * maximized window. Hidden windows keep their state but skip re-renders and effects.
 */
export function isWindowInView({
  focusedWindowId,
  isFocusedWindowMaximized,
  windowState,
}: {
  focusedWindowId: string | undefined;
  isFocusedWindowMaximized: boolean;
  windowState: { id: string; isMinimized: boolean };
}) {
  if (windowState.isMinimized) {
    return false;
  }

  return !isFocusedWindowMaximized || focusedWindowId === windowState.id;
}

export function shouldRenderWindowFrame({
  mountContent,
  windowState,
}: {
  mountContent: boolean;
  windowState: { id: string; isMinimized: boolean };
}) {
  if (windowState.id !== 'pvsim') {
    return true;
  }

  if (windowState.isMinimized) {
    return true;
  }

  return mountContent;
}

export function shouldAnimateWindowOnMount(
  windowState: { id: string; createdAt: string; updatedAt: string },
  animatedWindowCreatedAts: Map<string, string>,
) {
  if (windowState.createdAt !== windowState.updatedAt) {
    animatedWindowCreatedAts.set(windowState.id, windowState.createdAt);
    return false;
  }

  const previousCreatedAt = animatedWindowCreatedAts.get(windowState.id);
  if (previousCreatedAt === windowState.createdAt) {
    return false;
  }

  animatedWindowCreatedAts.set(windowState.id, windowState.createdAt);
  return true;
}
